/**
 * ActivityTimelineWidget
 *
 * Displays recent activity timeline for guides (bookings, messages, reviews).
 * Shows latest 10 activities with option to view all.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: SCRUM-529 - Activity Timeline (8 SP)
 * Location: src/components/guides/ActivityTimelineWidget.jsx
 */

import React, { useEffect, useState } from 'react';
import { Clock, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import apiClient from '../../utils/apiClient';

export function ActivityTimelineWidget({ guideId, onViewAll }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActivities();
  }, [guideId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/dashboard/activity-timeline', {
        params: { limit: 10 }
      });

      if (response.data.success) {
        setActivities(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching activity timeline:', err);
      setError(err.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {return 'Just now';}
    if (diffMins < 60) {return `${diffMins}m ago`;}
    if (diffHours < 24) {return `${diffHours}h ago`;}
    if (diffDays < 7) {return `${diffDays}d ago`;}

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActivityStyles = (color) => {
    const styles = {
      yellow: { bg: 'var(--color-warning-light)', text: 'var(--color-warning-dark)' },
      green: { bg: 'var(--color-success-light)', text: 'var(--color-success-dark)' },
      red: { bg: 'var(--color-error-light)', text: 'var(--color-error-dark)' },
      blue: { bg: 'var(--color-info-light)', text: 'var(--color-info-dark)' },
      purple: { bg: '#F3E8FF', text: '#7C3AED' }
    };
    return styles[color] || styles.blue;
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--color-border-light)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <Clock size={20} />
          Recent Activity
        </h3>
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--color-border-light)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <Clock size={20} />
          Recent Activity
        </h3>
        <Alert variant="error" message={error} />
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--color-white)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-border-light)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-4)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          margin: 0
        }}>
          <Clock size={20} />
          Recent Activity
        </h3>
        {activities.length > 0 && onViewAll && (
          <button
            onClick={onViewAll}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'transparent',
              color: 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              transition: 'all var(--transition-fast)'
            }}
            onMouseOver={(  _e) => {
              _e.currentTarget.style.backgroundColor = 'var(--color-primary-light)';
            }}
            onMouseOut={(  _e) => {
              _e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            View All <ArrowRight size={14} />
          </button>
        )}
      </div>

      {/* Activity List */}
      {activities.length === 0 ? (
        <div style={{
          padding: 'var(--space-8)',
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)'
        }}>
          No recent activity to display
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {activities.map((activity) => {
            const styles = getActivityStyles(activity.color);
            return (
              <div
                key={activity.id}
                style={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border-light)',
                  backgroundColor: 'var(--color-gray-50)',
                  transition: 'all var(--transition-fast)',
                  cursor: activity.link ? 'pointer' : 'default'
                }}
                onClick={() => activity.link && (window.location.href = activity.link)}
                onMouseOver={(  _e) => {
                  if (activity.link) {
                    _e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                    _e.currentTarget.style.borderColor = 'var(--color-primary-light)';
                  }
                }}
                onMouseOut={(  _e) => {
                  if (activity.link) {
                    _e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                    _e.currentTarget.style.borderColor = 'var(--color-border-light)';
                  }
                }}
              >
                {/* Icon */}
                <div style={{
                  minWidth: '36px',
                  height: '36px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: styles.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  {activity.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--space-1)'
                  }}>
                    {activity.title}
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {activity.description}
                  </div>
                </div>

                {/* Timestamp */}
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-tertiary)',
                  whiteSpace: 'nowrap',
                  marginTop: '2px'
                }}>
                  {formatTimestamp(activity.timestamp)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ActivityTimelineWidget;
